import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  apiLogin,
  apiLogout,
  apiMe,
  apiRegister,
  apiRefreshCsrf,
  formatApiError,
  setCachedCsrf,
  setUnauthHandler,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // status: "loading" | "authed" | "guest"
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);

  const doLogout = useCallback(() => {
    setCachedCsrf(null);
    setUser(null);
    setStatus("guest");
  }, []);

  useEffect(() => {
    setUnauthHandler(doLogout);
  }, [doLogout]);

  // Bootstrap: always ask /me — the session cookie (if any) is httpOnly so we
  // can't inspect it directly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { user: me } = await apiMe();
        if (cancelled) return;
        setUser(me);
        setStatus("authed");
        // Refresh CSRF cookie in case document.cookie was cleared (some
        // preview browsers strip non-httpOnly cookies on hard reload).
        try {
          await apiRefreshCsrf();
        } catch {
          /* non-fatal */
        }
      } catch {
        if (!cancelled) doLogout();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doLogout]);

  const login = async (email, password) => {
    const { user: me } = await apiLogin({ email, password });
    setUser(me);
    setStatus("authed");
    return me;
  };

  const register = async (payload) => {
    const { user: me } = await apiRegister(payload);
    setUser(me);
    setStatus("authed");
    return me;
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch {
      /* client-side logout authoritative */
    }
    doLogout();
  };

  const refreshMe = async () => {
    const { user: me } = await apiMe();
    setUser(me);
    return me;
  };

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        isMaster: user?.role === "master",
        login,
        register,
        logout,
        refreshMe,
        formatApiError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
};
